export type SourceNameAndSourceId<TSourceName extends string> = {
  sourceName: TSourceName;
  sourceId: string;
};

export type SourceKey<TSourceName extends string> = `${TSourceName}||${string}`;

const hasSourceNameAndSourceId = <TSourceName extends string>(
  it: any,
): it is SourceNameAndSourceId<TSourceName> => !!it.sourceName && !!it.sourceId;

export const getSourceNameAndSourceId = <TSourceName extends string>(
  it: SourceNameAndSourceId<TSourceName>,
): SourceNameAndSourceId<TSourceName> => {
  if (hasSourceNameAndSourceId(it)) {
    return { sourceName: it.sourceName, sourceId: it.sourceId };
  }

  throw new Error(
    `Unable to extract sourceName and sourceId from ${JSON.stringify(it)}`,
  );
};

export const getSourceKey = <TSourceName extends string>(
  it: SourceNameAndSourceId<TSourceName>,
): SourceKey<TSourceName> => {
  const { sourceName, sourceId } = getSourceNameAndSourceId(it);

  return `${sourceName}||${sourceId}`;
};

export const getSourceNameAndSourceIdFromSourceKey = <
  TSourceName extends string,
>(
  sourceKey: SourceKey<TSourceName>,
): SourceNameAndSourceId<TSourceName> => {
  const [sourceName, sourceId] = sourceKey.split('||') as [TSourceName, string];
  if (sourceName && sourceId) {
    return { sourceName, sourceId };
  }

  throw new Error(
    `Unable to extract sourceName and sourceId from ${sourceKey} - expected format is "sourceName||sourceId"`,
  );
};

export const getMapBySourceKey = <
  TSourceName extends string,
  T extends SourceNameAndSourceId<TSourceName>,
  TMapped = T,
>(
  items: T[],
  options?: {
    mapValue?: (item: T) => TMapped;
  },
): Map<SourceKey<TSourceName>, TMapped> =>
  new Map(
    items.map((it) => [
      getSourceKey(it),
      (options?.mapValue?.(it) ?? it) as TMapped,
    ]),
  );
